// Filtro de envío (QA): si la lista NO está vacía, SOLO se envía WhatsApp a estos números.
// Formato: bloque local de 8 dígitos SIN el 9 (callNotification antepone '+569' al enviar).
const QA_OVERRIDE_PHONE: number | null = null // Si es null, usa el teléfono del destinatario validando con allowedNumbers
const allowedNumbers: number[] = [86526143, 65887723, 50207581, 51361289]

const isAllowed = (num: number | string) => {
  const digits = String(num).replace(/\D/g, '')
  const norm = parseInt(digits.length >= 8 ? digits.slice(-8) : digits, 10)
  return allowedNumbers.includes(norm)
}

interface WANotification {
  id: number // template_id real en el deflector onemarketer = "Id Meta" del Sheet templates_vfinal_v1
  name: string // "Name in FB" (nombre del template aprobado en Meta)
  params: number // nº mínimo de variables; INCLUYE el parámetro de URL del botón dinámico si lo hay
  template: string // texto de referencia (NO se envía: el deflector usa el cuerpo registrado en Meta)
  // Refleja la columna "Visible" del Sheet: false = Deshabilitado en Meta. callNotification NO
  // golpea el deflector para los deshabilitados (los registra como 'omitido'), de modo que el wiring
  // puede quedar preparado en los call-sites sin activarse hasta que Meta los habilite. undefined = habilitado.
  enabled?: boolean
  vars?: string // documentación: qué dato corresponde a cada var_body_N (orden posicional)
}

// ─────────────────────────────────────────────────────────────────────────────
// Catálogo de plantillas WhatsApp — versión FINAL (Sheet templates_vfinal_v1,
// ID 1F16OfC10JBu1k4P5Qk4Kz4_K0pito93pylwjdd6oIMM, pestaña "templates").
// Sincronizado 2026-06-10 (milestone G17). El campo `id` ES el template_id que se
// envía al deflector onemarketer; coincide con la columna "Id Meta" del Sheet.
//
// CONTRATO DE VARIABLES — los templates con botón dinámico de URL exigen un parámetro
// EXTRA al final de `variables` con el SUFIJO de URL del botón (verificado contra el
// deflector 2026-06-10: sin él devuelve code 209 "Variables de template inválidas").
//   • 95/96 (botón "Ingresar"):        var_body_1..3 + sufijo-URL  → params 4
//   • 97   (botón "Recuperar contraseña"): var_body_1 + sufijo-URL  → params 2
//
// HABILITADOS en Meta (Visible=Habilitado): 45,47,58,59,60,61,62,63,64,65,66,68,74,95,96,97.
// DESHABILITADOS (Visible=Deshabilitado): 77,81-94,98-106 — quedan en el catálogo para el
// triage y el wiring preparado, pero callNotification NO los envía (enabled:false).
// Legacy local: 69,70,73,75 YA NO se referencian (migrados a 47/45/63/60 en el resync G17) y
// pueden eliminarse del catálogo en una limpieza posterior. 76 (aviso al admin de nueva solicitud
// de representante) tampoco tiene call-site activo y sigue sin equivalente final en el Sheet.
// ─────────────────────────────────────────────────────────────────────────────
const definedNotifications: Array<WANotification> = [
  // ── HABILITADOS ────────────────────────────────────────────────────────────
  {
    id: 45,
    name: 'user_creacion_sin_doc_v1',
    params: 1,
    enabled: true,
    vars: 'var_body_1 = nombre del usuario',
    template:
      '*Hola [var_body_1]:* \n Tu usuario ha sido creado en el sistema, pero aún faltan documentos para completar el registro. \n\nPor favor, sube los antecedentes pendientes para continuar con el proceso de aprobación.',
  },
  {
    id: 47,
    name: 'user_rechazo_v1', // language es_CL
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre del armador · var_body_2 = motivo del rechazo',
    template:
      'Hola [var_body_1]:\nTu registro como armador ha sido rechazado.\n\n*Motivo:* [var_body_2].\n\nTe invitamos a revisar la información y volver a enviar tu solicitud.',
  },
  {
    id: 58,
    name: 'user_creacion_doc_pendientes_v1',
    params: 1,
    enabled: true,
    vars: 'var_body_1 = nombre del usuario',
    template:
      '📑Hola [var_body_1],\n tu usuario fue creado, pero faltan documentos para completar el registro.\n Sube los antecedentes pendientes para continuar con la revisión.',
  },
  {
    id: 59,
    name: 'user_creacion_ok_v1',
    params: 1,
    enabled: true,
    vars: 'var_body_1 = nombre del usuario',
    template:
      'Hola [var_body_1] 👋,\n tu usuario fue creado correctamente.\n Recibirás una notificación cuando el administrador revise y apruebe tu solicitud.',
  },
  {
    id: 60,
    name: 'creacion_usuario_adm',
    params: 3,
    enabled: true,
    vars: 'var_body_1 = nombre · var_body_2 = usuario · var_body_3 = contraseña',
    template:
      '✅ Hola [var_body_1], tu registro como armador ha sido creado y aprobado por el administrador.\n\n Puedes ingresar al sistema con las siguientes credenciales:\n\n Usuario:[var_body_2]\nContraseña: [var_body_3]\n\nTe recomendamos cambiar tu contraseña al primer ingreso.\n\n ¡Bienvenido al sistema de gestión turística del Muelle Prat! 🚤',
  },
  {
    id: 61,
    name: 'emb_aprobado_v2',
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre de la embarcación',
    template:
      '✅Hola [var_body_1],\nla embarcación [var_body_2] fue aprobada por el administrador.\n\n Puedes gestionar tus embarcaciones y tripulantes directamente en el sistema.',
  },
  {
    id: 62,
    name: 'user_aprobado_v4',
    params: 1,
    enabled: true,
    vars: 'var_body_1 = nombre del armador',
    template:
      '✅Hola [var_body_1],\ntu registro como armador fue aprobado.\n\n Ya puedes acceder al sistema y gestionar tus embarcaciones y tripulantes.',
  },
  {
    id: 63,
    name: 'emb_creacion_doc_pendientes_v2',
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre de la embarcación',
    template:
      '📑Hola [var_body_1],\n la embarcación [var_body_2] fue registrada, pero faltan documentos para completar el proceso.\n\n Sube los antecedentes pendientes para continuar con la revisión.',
  },
  {
    id: 64,
    name: 'trip_aprobado_v2',
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre del tripulante',
    template:
      '✅Hola [var_body_1],\n el tripulante [var_body_2] fue aprobado por el administrador.\n\n Debes coordinar con el administrador la entrega de la credencial correspondiente.\n\n Recuerda: puedes gestionar tus embarcaciones y tripulantes directamente en el sistema.',
  },
  {
    id: 65,
    name: 'trip_creacion_doc_pendientes_v1',
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre del tripulante',
    template:
      '📑Hola [var_body_1],\nel tripulante [var_body_2] fue registrado, pero faltan documentos para completar el proceso.\n\n Sube los antecedentes pendientes para continuar con la revisión.',
  },
  {
    id: 66,
    name: 'emb_rechazo_v3',
    params: 3,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre de la embarcación · var_body_3 = motivo',
    template:
      '⚠️Hola [var_body_1],\n la embarcación [var_body_2] fue rechazada.\n\n Motivo: [var_body_3].\n\n Corrige la información y vuelve a enviar la solicitud.',
  },
  {
    id: 68,
    name: 'trip_rechazo_v2',
    params: 3,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre del tripulante · var_body_3 = motivo',
    template:
      '⚠️Hola [var_body_1],\n el tripulante [var_body_2] fue rechazado.\n\n *Motivo:* [var_body_3].\n\n Revisa los datos y vuelve a enviar la solicitud.',
  },
  {
    id: 74,
    name: 'deuda_v5',
    params: 3,
    enabled: true,
    vars: 'var_body_1 = nombre · var_body_2 = mes · var_body_3 = monto total adeudado',
    template:
      '💰Hola [var_body_1],\n se registra un monto adeudado por el uso del o los embarcaderos del Muelle Prat correspondiente al mes de [var_body_2], por un total de [var_body_3].\n\n Puedes revisar el detalle en la ventana Facturación del sistema.\n\n Te solicitamos regularizar el pago a la brevedad.',
  },
  {
    id: 95,
    name: 'notificacion_asignacion_apoderado_v3', // asignación por el ARMADOR · botón "Ingresar"
    params: 4,
    enabled: true,
    vars: 'var_body_1 = nombre del apoderado · var_body_2 = armador · var_body_3 = embarcación · var_body_4 = sufijo URL del botón "Ingresar"',
    template:
      'Hola, *[var_body_1]*.\n\nEl armador *[var_body_2]* te ha designado como apoderado de la embarcación *[var_body_3]* en el sistema del Muelle Prat.\n\nEsto significa que podrás realizar gestiones en su nombre dentro del sistema.\n\nPara acceder, selecciona el botón *"Ingresar"*.',
  },
  {
    id: 96,
    name: 'notificacion_asignacion_apoderado_por_admin_v2', // asignación por el ADMIN · botón "Ingresar"
    params: 4,
    enabled: true,
    vars: 'var_body_1 = nombre del apoderado · var_body_2 = embarcación · var_body_3 = armador · var_body_4 = sufijo URL del botón "Ingresar"',
    template:
      'Hola, *[var_body_1]*.\n\nEl administrador del sistema te ha designado como apoderado de la embarcación *[var_body_2]*, del armador *[var_body_3]*.\n\nEsto significa que podrás realizar gestiones en su nombre dentro del sistema para esa embarcación.\n\nPara acceder, selecciona el botón *"Ingresar"*.',
  },
  {
    id: 97,
    name: 'recuperacion_user_v1', // botón "Recuperar contraseña"
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre del usuario · var_body_2 = sufijo URL del botón "Recuperar contraseña" (token)',
    template:
      'Hola, *[var_body_1]*.\n\nHemos recibido una solicitud para restablecer la contraseña de tu cuenta.\n\nPara continuar, selecciona el botón *"Recuperar contraseña"*.',
  },

  // ── LEGACY local (sin equivalente final confirmado / pendientes de migrar) ───
  // 69,70,73,75 colisionan conceptualmente con 47/45/63/60 finales; se mantienen
  // mientras sus call-sites no migren en el wiring de G17. 76 (aviso al admin de
  // nueva solicitud de representante) no tiene equivalente en el Sheet final.
  {
    id: 69,
    name: 'user_rechazo_v4', // DEPRECATED — migrar call-sites de rechazo de armador a 47 (user_rechazo_v1)
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre del armador · var_body_2 = motivo',
    template:
      '⚠️Hola [var_body_1],\n tu registro como armador fue rechazado.\n\n *Motivo:* [var_body_2]\n\n Revisa la información y vuelve a enviar la solicitud.',
  },
  {
    id: 70,
    name: 'user_creacion_sin_doc_v3', // DEPRECATED — equivalente final 45/58
    params: 1,
    enabled: true,
    vars: 'var_body_1 = nombre del usuario',
    template:
      '📑Hola [var_body_1]:\n Tu usuario ha sido creado en el sistema, pero aún faltan documentos para completar el registro.\n\n Por favor, sube los antecedentes pendientes para continuar con el proceso de aprobación.',
  },
  {
    id: 73,
    name: 'emb_creacion_doc_pendientes_v3', // DEPRECATED — equivalente final 63
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre de la embarcación',
    template:
      '📑Hola [var_body_1],\n la embarcación [var_body_2] fue registrada, pero faltan documentos para completar el proceso.\n\n Sube los antecedentes pendientes para continuar con la revisión.',
  },
  {
    id: 75,
    name: 'user_creacion_adm_v3', // DEPRECATED — equivalente final 60 (creacion_usuario_adm)
    params: 3,
    enabled: true,
    vars: 'var_body_1 = nombre · var_body_2 = usuario · var_body_3 = contraseña temporal',
    template:
      'Hola [var_body_1] 👋,\n tu registro como armador fue creado y aprobado por el administrador.\n Se ha habilitado una cuenta para tu uso en el sistema de gestión turística del Muelle Prat.\n\n Usuario asignado: [var_body_2]\nContraseña temporal: [var_body_3]\n\n Te recomendamos actualizar tu contraseña desde el sistema en tu primer ingreso.',
  },
  {
    id: 76,
    name: 'rep_sol_admin_v1', // aviso al ADMIN de nueva solicitud de representante (sin equivalente final)
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre del admin · var_body_2 = nombre del representante propuesto',
    template:
      '📋 Nueva solicitud de representante\n\nHola [var_body_1],\n se ha enviado una solicitud para designar a [var_body_2] como representante en el sistema del Muelle Prat.\n\n Por favor, revisa la solicitud en el panel de administración.',
  },

  // ── DESHABILITADOS en Meta (wiring preparado, NO se envían) ──────────────────
  // Triage G17: clasificación de versiones viejas vs flujos nuevos pendientes de habilitar.
  {
    id: 77,
    name: 'user_block_venc_emb_v1', // PENDIENTE Meta — aviso de vencimiento de certificado de navegabilidad
    params: 3,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = embarcación · var_body_3 = fecha de vencimiento',
    template:
      '👋Hola [var_body_1],\nse recuerda que el certificado de navegabilidad de la embarcación [var_body_2] vence el [var_body_3].\n\nDebes cargar el documento actualizado antes de que la autorización de operación de la embarcación quede suspendida.',
  },
  {
    id: 81,
    name: 'registro_representante_deshabilitacion_v1', // PENDIENTE Meta
    params: 2,
    enabled: false,
    vars: 'var_body_1 = nombre del representante · var_body_2 = armador',
    template:
      'Hola, [var_body_1].\n\nTe informamos que tu acceso como representante del armador [var_body_2] en el sistema del Muelle Prat ha sido deshabilitado.\n\nA contar de este momento, no podrás acceder ni operar en el sistema en representación de dicho armador.\n\nPara más información, puedes contactar al armador correspondiente.',
  },
  {
    id: 82,
    name: 'registro_representante_mas_emb_v1', // PENDIENTE Meta
    params: 2,
    enabled: false,
    vars: 'var_body_1 = nombre del representante · var_body_2 = armador',
    template:
      'Hola 👋, [var_body_1].\n\nTe informamos que el armador [var_body_2] te ha designado como su representante. Revisa las embarcaciones que puedes gestionar en el sistema. \n\nPara ingresar, usa como contraseña los 4 últimos dígitos de tu Rut (sin dígito verificador).\n\nPor seguridad, deberás cambiar tu contraseña al ingresar por primera vez.',
  },
  {
    id: 83,
    name: 'registro_emb_incompleto_v1', // PENDIENTE Meta
    params: 2,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = embarcación',
    template:
      'Hola, [var_body_1].\n\nEl registro de la embarcación [var_body_2] se encuentra incompleto, ya que faltan antecedentes por ingresar o actualizar.\n\nTe invitamos a completar la información requerida en el sistema para continuar con el proceso.',
  },
  {
    id: 84,
    name: 'registro_tripulante_vencimiento_v1', // PENDIENTE Meta
    params: 4,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = documento · var_body_3 = tripulante · var_body_4 = días para el vencimiento',
    template:
      'Hola, [var_body_1].\n\nTe informamos que el documento [var_body_2] del tripulante [var_body_3] vence en [var_body_4] días.\n\nTe recomendamos actualizar esta información oportunamente para evitar restricciones en su habilitación.',
  },
  {
    id: 85,
    name: 'registro_tripulante_incompleto_v1', // PENDIENTE Meta
    params: 3,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = tipo (tripulante/...) · var_body_3 = nombre del tripulante',
    template:
      'Hola, [var_body_1].\n\nEl registro del [var_body_2], [var_body_3], se encuentra incompleto, ya que faltan antecedentes por ingresar o actualizar.\n\nTe invitamos a completar la información requerida en el sistema para continuar con el proceso.',
  },
  {
    id: 86,
    name: 'registro_tripulante_rechazado_v1', // DESCARTAR — duplicado de 68 (trip_rechazo_v2, habilitado)
    params: 3,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = tripulante · var_body_3 = motivo',
    template:
      'Hola, [var_body_1].\n\nEl tripulante [var_body_2] fue rechazado.\n\n*Motivo:* [var_body_3].\n\nRevisa los antecedentes y vuelve a enviar la solicitud.',
  },
  {
    id: 87,
    name: 'registro_tripulante_aprobado_v1', // DESCARTAR — versión vieja de 64 (trip_aprobado_v2, habilitado)
    params: 3,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = tripulante · var_body_3 = días hábiles de emisión de la credencial',
    template:
      'Hola 👋, [var_body_1] .\nEl tripulante [var_body_2] fue aprobado por el administrador.\n\nDebes coordinar con el administrador la entrega de la credencial correspondiente.\n\nTen en cuenta que la emisión de la credencial demora [var_body_3] días hábiles desde que fue aprobado el registro.',
  },
  {
    id: 88,
    name: 'registro_tripulante_v1', // PENDIENTE Meta — acuse de registro de tripulante (pendiente de aprobación)
    params: 2,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = tripulante',
    template:
      'Hola 👋, [var_body_1].\n\nTe informamos que el registro del tripulante [var_body_2] ha sido ingresado correctamente y se encuentra pendiente de aprobación por parte del administrador.\n\nTe notificaremos por este medio una vez finalizada la revisión.',
  },
  {
    id: 89,
    name: 'registro_armador_adm_incompleto_v1', // PENDIENTE Meta
    params: 1,
    enabled: false,
    vars: 'var_body_1 = nombre del armador',
    template:
      'Hola, [var_body_1].\n\nSe ha creado tu cuenta en el sistema del Muelle Prat; sin embargo, tu registro se encuentra incompleto, ya que faltan antecedentes por ingresar o actualizar.\n\nPara utilizar el sistema, deberás completar la información requerida. \n\nPara ingresar, usa como contraseña los 4 últimos dígitos de tu Rut (sin dígito verificador).\n\nPor seguridad, deberás cambiar tu contraseña al ingresar por primera vez.',
  },
  {
    id: 90,
    name: 'registro_armador_adm_v1', // DESCARTAR — versión vieja de creacion_usuario_adm (60) sin credenciales explícitas
    params: 1,
    enabled: false,
    vars: 'var_body_1 = nombre del armador',
    template:
      'Hola 👋, [var_body_1].\n\nSe ha creado tu cuenta en el sistema del Muelle Prat.\n\nPara ingresar, usa como contraseña los 4 últimos dígitos de tu Rut (sin dígito verificador).\n\nPor seguridad, deberás cambiar tu contraseña al ingresar por primera vez.',
  },
  {
    id: 91,
    name: 'registro_armador_incompleto_v2', // DESCARTAR — versión vieja de 45/58 (user_creacion_*_v1)
    params: 1,
    enabled: false,
    vars: 'var_body_1 = nombre del armador',
    template:
      'Hola, [var_body_1].\n\nTu registro ha sido creado en el sistema, pero aún faltan documentos para completar el proceso.\n\nPara continuar y poder solicitar una Autorización de Operación, debes completar la información requerida en el sistema.',
  },
  {
    id: 92,
    name: 'registro_armador_correcto_v1', // DESCARTAR — versión vieja de 59 (user_creacion_ok_v1)
    params: 1,
    enabled: false,
    vars: 'var_body_1 = nombre del armador',
    template:
      'Hola 👋, [var_body_1].\n\nTu registro como armador ha sido creado correctamente en el sistema.\n\nLa validación de tus antecedentes se realizará en el marco de la solicitud de Autorización de Operación que ingreses.',
  },
  {
    id: 93,
    name: 'aviso_representante_deshabilitado_1', // PENDIENTE Meta
    params: 2,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = embarcación',
    template:
      'Hola [var_body_1],\nTe informamos que ya no te encuentras habilitado como representante de la embarcación [var_body_2] en el Sistema de Gestión Turística del Muelle Prat.',
  },
  {
    id: 94,
    name: 'registro_tripulante_vencimiento_v3', // PENDIENTE Meta — versión más detallada de 84
    params: 5,
    enabled: false,
    vars: 'var_body_1 = nombre · var_body_2 = documento · var_body_3 = tipo · var_body_4 = nombre · var_body_5 = fecha de vencimiento',
    template:
      'Hola, [var_body_1].\n\nTe informamos que el documento [var_body_2] del [var_body_3], [var_body_4], vence el [var_body_5].\n\nTe recomendamos actualizar esta información oportunamente para evitar restricciones en su habilitación.',
  },
  // Apoderados — ciclo de vida (PENDIENTE habilitación Meta). Notifican al ARMADOR salvo
  // 100/105/106 que notifican al APODERADO. 105/106 son versiones VIEJAS de 96/95
  // (enlace en el cuerpo en vez de botón) → DESCARTAR una vez confirmados 95/96.
  {
    id: 98,
    name: 'notificacion_renuncia_armador_v1', // notifica al armador que su apoderado renunció
    params: 3,
    enabled: false,
    vars: 'var_body_1 = armador · var_body_2 = apoderado · var_body_3 = embarcación',
    template:
      'Hola, [var_body_1].\n\nTe informamos que el apoderado [var_body_2] ha renunciado a su designación como apoderado de la embarcación [var_body_3] en el sistema del Muelle Prat.\n\nA contar de este momento, ya no podrá realizar gestiones en tu nombre respecto de dicha embarcación.',
  },
  {
    id: 99,
    name: 'notificacion_remocion_armador_por_admin_v1', // notifica al armador que el admin removió a su apoderado
    params: 3,
    enabled: false,
    vars: 'var_body_1 = armador · var_body_2 = apoderado · var_body_3 = embarcación',
    template:
      'Hola, [var_body_1].\n\nEl administrador del sistema ha removido como apoderado a [var_body_2] de la embarcación [var_body_3].\n\nEsto significa que esta persona ya no podrá realizar gestiones en tu nombre para esa embarcación.',
  },
  {
    id: 100,
    name: 'notificacion_remocion_apoderado_por_admin_v1', // notifica al APODERADO que el admin lo removió
    params: 3,
    enabled: false,
    vars: 'var_body_1 = apoderado · var_body_2 = embarcación · var_body_3 = armador',
    template:
      'Hola, [var_body_1].\n\nEl administrador del sistema ha dejado sin efecto tu designación como apoderado de la embarcación [var_body_2], del armador [var_body_3].\n\nEsto significa que ya no podrás realizar gestiones en su nombre para esa embarcación.',
  },
  {
    id: 101,
    name: 'notificacion_remocion_apoderado_v1', // notifica al APODERADO que el armador lo removió
    params: 3,
    enabled: false,
    vars: 'var_body_1 = apoderado · var_body_2 = armador · var_body_3 = embarcación',
    template:
      'Hola, [var_body_1].\n\nTe informamos que el armador [var_body_2] ha dejado sin efecto tu designación como apoderado de la embarcación [var_body_3] en el sistema del Muelle Prat.\n\nA contar de este momento, ya no podrás realizar gestiones en su nombre respecto de dicha embarcación.\n\nPara más información, puedes contactar al armador correspondiente.',
  },
  {
    id: 102,
    name: 'rechazo_representante_armador_por_admin_v1', // notifica al armador el rechazo del apoderado
    params: 3,
    enabled: false,
    vars: 'var_body_1 = armador · var_body_2 = apoderado · var_body_3 = embarcación',
    template:
      'Hola, [var_body_1].\n\nEl administrador del sistema ha rechazado el registro del apoderado [var_body_2] para la embarcación [var_body_3].\n\nEsto significa que no podrá actuar en tu nombre para esa embarcación.\n\nPuedes revisar el detalle en el sistema.',
  },
  {
    id: 103,
    name: 'aprobacion_representante_armador_por_admin_v1', // notifica al armador la aprobación del apoderado
    params: 3,
    enabled: false,
    vars: 'var_body_1 = armador · var_body_2 = apoderado · var_body_3 = embarcación',
    template:
      'Hola, [var_body_1].\n\nEl administrador del sistema ha aprobado al apoderado [var_body_2] para la embarcación [var_body_3].\n\nEsto significa que ya puede realizar gestiones en tu nombre dentro del sistema para esa embarcación.',
  },
  {
    id: 104,
    name: 'notificacion_asignacion_armador_por_admin_v1', // notifica al armador que el admin asignó un apoderado
    params: 3,
    enabled: false,
    vars: 'var_body_1 = armador · var_body_2 = apoderado · var_body_3 = embarcación',
    template:
      'Hola, [var_body_1].\n\nEl administrador del sistema ha asignado como apoderado a [var_body_2] para la embarcación [var_body_3].\n\nEsto significa que esta persona podrá realizar gestiones en tu nombre dentro del sistema para esa embarcación.',
  },
  {
    id: 105,
    name: 'notificacion_asignacion_apoderado_por_admin_v1', // DESCARTAR — versión vieja de 96 (enlace en cuerpo)
    params: 4,
    enabled: false,
    vars: 'var_body_1 = apoderado · var_body_2 = embarcación · var_body_3 = armador · var_body_4 = enlace',
    template:
      'Hola, [var_body_1].\n\nEl administrador del sistema te ha designado como apoderado de la embarcación [var_body_2], del armador [var_body_3].\n\nEsto significa que podrás realizar gestiones en su nombre dentro del sistema para esa embarcación.\n\nPara acceder, ingresa al sistema a través del siguiente enlace:',
  },
  {
    id: 106,
    name: 'notificacion_asignacion_apoderado_v1', // DESCARTAR — versión vieja de 95 (enlace en cuerpo)
    params: 4,
    enabled: false,
    vars: 'var_body_1 = apoderado · var_body_2 = armador · var_body_3 = embarcación · var_body_4 = enlace',
    template:
      'Hola, [var_body_1].\n\nEl armador [var_body_2] te ha designado como apoderado de la embarcación [var_body_3] en el sistema del Muelle Prat.\n\nEsto significa que podrás realizar gestiones en su nombre dentro del sistema.\n\nPara acceder, ingresa al sistema a través del siguiente enlace:',
  },

  // ── REGLAMENTO ETAPA 1 — PLANTILLAS APROBADAS META ─────────────────────────
  {
    id: 126,
    name: 'aprobacion_ao_v1', // PP-AO-02: Aprobación de Autorización de Operación
    params: 4,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre embarcación · var_body_3 = fecha inicio (dd/mm/aaaa) · var_body_4 = fecha vencimiento (dd/mm/aaaa)',
    template:
      '*Hola, [var_body_1]:*\n\nTe informamos que la solicitud de *Autorización de Operación* de la embarcación *[var_body_2]* fue aprobada.\n\nLa autorización se encuentra vigente desde el *[var_body_3]* hasta el *[var_body_4]*.\n\nPuede descargar el documento en el sistema.\n\nSaludos',
  },
  {
    id: 128,
    name: 'suspension_ao_cn_v1', // PP-AO-15: Suspensión automática de AO por Certificado de Navegabilidad vencido
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre embarcación',
    template:
      '⚠️ Hola [var_body_1],\n\nTe informamos que la Autorización de Operación de tu embarcación *[var_body_2]* ha quedado *SUSPENDIDA* debido al vencimiento de su Certificado de Navegabilidad (CN).\n\nPara restablecer la vigencia, debes cargar en el sistema el nuevo Certificado emitido por DIRECTEMAR.',
  },
  {
    id: 129,
    name: 'aviso_vencimiento_cn_v1', // PP-AO-16: Alerta 7 días antes de vencimiento de Certificado de Navegabilidad
    params: 3,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre embarcación · var_body_3 = fecha vencimiento',
    template:
      '⏰ Hola [var_body_1],\n\nTe recordamos que el Certificado de Navegabilidad de tu embarcación *[var_body_2]* vencerá el *[var_body_3]* (en 7 días).\n\nTe solicitamos renovar y cargar oportunamente el nuevo certificado en el sistema para evitar la suspensión automática de su Autorización de Operación.',
  },
  {
    id: 130,
    name: 'tripulante_aprobado_v1', // PP-TR-03, PP-TR-10: Aprobación de Tripulante
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre tripulante',
    template:
      '✅ Hola [var_body_1],\n\nEl tripulante *[var_body_2]* ha sido *APROBADO* por la administración para operar en el Muelle Prat.\n\nEl tripulante ya se encuentra habilitado para formar parte de dotaciones de zarpe.',
  },
  {
    id: 131,
    name: 'tripulante_rechazado_v1', // PP-TR-04, PP-TR-10: Rechazo de Tripulante
    params: 3,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre tripulante · var_body_3 = motivo',
    template:
      '⚠️ Hola [var_body_1],\n\nLa solicitud de inscripción del tripulante *[var_body_2]* ha sido *RECHAZADA* por la administración.\n\n*Motivo:* [var_body_3].\n\nPuedes corregir los antecedentes y volver a enviar la solicitud.',
  },
  {
    id: 132,
    name: 'ao_vigente_de_nuevo_v3', // PP-AO-07, PP-AO-17: Levantamiento de suspensión / Restablecimiento de vigencia
    params: 2,
    enabled: true,
    vars: 'var_body_1 = nombre destinatario · var_body_2 = nombre embarcación',
    template:
      '✅ Hola [var_body_1],\n\nTe informamos que la Autorización de Operación (AO) de tu embarcación *[var_body_2]* se encuentra nuevamente *VIGENTE*.\n\nLa embarcación queda plenamente habilitada para operar en el Muelle Prat.',
  },
]

interface notificationResponse {
  deflector_data_id: number
  message_id: number
  status: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Historial interno de envíos (WhatsApp / correo).
// Cada intento se registra en muelledb.notificaciones_envios vía un endpoint PHP
// dedicado (log_envio.php), alcanzado por el mismo proxy CORS que el resto del
// backend. Es best-effort: nunca debe interrumpir el flujo de notificación.
// Se activa solo si VITE_LOG_ENVIOS_URL está definida (ver .env.qa). En prod queda
// deshabilitado hasta desplegar log_envio.php + la tabla y configurar la URL.
// ─────────────────────────────────────────────────────────────────────────────
const LOG_ENVIOS_URL = import.meta.env.VITE_LOG_ENVIOS_URL || ''

export interface EnvioHistorial {
  canal: 'whatsapp' | 'correo'
  receptor: string
  resultado: 'enviado' | 'error' | 'omitido'
  template_id?: number
  template_nombre?: string
  variables?: string[]
  respuesta?: unknown
  message_id?: string
  contexto?: string
}

/** Registra un envío en el historial interno. No lanza ni bloquea (fire-and-forget). */
export function logEnvioHistorial(entry: EnvioHistorial): void {
  if (!LOG_ENVIOS_URL) return
  try {
    void fetch(LOG_ENVIOS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entry),
    }).catch((err) => console.warn('No se pudo registrar el envío en el historial', err))
  } catch (err) {
    console.warn('No se pudo registrar el envío en el historial', err)
  }
}

class WANotificationsServices {
  constructor() {}

  public startup() {}

  async callNotification(id: number, telefono: number, ...vars: string[]): Promise<boolean> {
    const notification = definedNotifications.find((n) => n.id == id)
    const rawNum = QA_OVERRIDE_PHONE !== null ? QA_OVERRIDE_PHONE : telefono
    const digitsOnly = String(rawNum || '').replace(/\D/g, '')
    const local8Digits = digitsOnly.length >= 8 ? digitsOnly.slice(-8) : digitsOnly
    const numeroFinal = parseInt(local8Digits, 10)
    const receptor = '+569' + local8Digits

    // Skip por allowlist (QA): se registra como 'omitido' para dejar rastro del intento.
    if (allowedNumbers.length > 0 && !isAllowed(numeroFinal)) {
      logEnvioHistorial({
        canal: 'whatsapp',
        receptor,
        resultado: 'omitido',
        template_id: id,
        template_nombre: notification?.name,
        variables: vars,
        contexto: 'omitido por allowlist QA',
      })
      return true // Skip sending for not allowed numbers, only if allowedNumbers is not empty
    }
    try {
      if (!notification) return false

      // Templates DESHABILITADOS en Meta: el wiring puede estar preparado en los call-sites,
      // pero no se envían (Meta los rechazaría). Se registra el intento como 'omitido' para
      // que el flujo quede listo y solo falte cambiar enabled→true cuando Meta los habilite.
      if (notification.enabled === false) {
        logEnvioHistorial({
          canal: 'whatsapp',
          receptor,
          resultado: 'omitido',
          template_id: id,
          template_nombre: notification.name,
          variables: vars,
          contexto: 'template deshabilitado en Meta',
        })
        return false
      }

      if (vars.length < notification.params) return false

      const body = {
        ani: receptor,
        template_id: id,
        variables: vars,
        parameters: {},
      }

      const response = await fetch('https://console.onemarketer.cl/epv/deflector', {
        method: 'POST',
        headers: {
          'content-type': 'application/json; charset=utf-8',
          API_KEY: import.meta.env.VITE_WS_KEY,
          API_SECRET: import.meta.env.VITE_WS_SECRET,
        },
        body: JSON.stringify(body),
      })

      const data: notificationResponse = await response.json()

      logEnvioHistorial({
        canal: 'whatsapp',
        receptor,
        resultado: data.status == 'success' ? 'enviado' : 'error',
        template_id: id,
        template_nombre: notification.name,
        variables: vars,
        respuesta: data,
        message_id: data?.message_id != null ? String(data.message_id) : undefined,
      })

      if (data.status == 'success') return true
    } catch (err) {
      console.error('Error sending notification', err)
      logEnvioHistorial({
        canal: 'whatsapp',
        receptor,
        resultado: 'error',
        template_id: id,
        template_nombre: notification?.name,
        variables: vars,
        respuesta: String(err),
      })
    }
    return false
  }
}

const whatsappNotificationManager = new WANotificationsServices()

export default whatsappNotificationManager
